/**
 * Pagination atom — compound primitives for page navigation.
 * Structure: Pagination (nav) > PaginationContent (ul) > PaginationItem (li) + links/controls.
 * Uses Button atom; page numbers and arrows share one fixed box size.
 */

import React from "react";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui";
import { IconWrapper } from "../IconWrapper";
import { getPaginationWindow } from "./paginationWindow";
import {
  getPaginationContentClassName,
  getPaginationEllipsisClassName,
  getPaginationLinkClassName,
  getPaginationNavClassName,
  getPaginationPrevNextClassName,
} from "./Pagination.styles";
import type {
  PaginationEllipsisProps,
  PaginationLinkProps,
  PaginationPageListProps,
  PaginationPrevNextProps,
} from "./Pagination.types";

function Pagination(props: React.ComponentProps<"nav">) {
  const { className, ...rest } = props;

  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={getPaginationNavClassName(className)}
      {...rest}
    />
  );
}

function PaginationContent(props: React.ComponentProps<"ul">) {
  const { className, ...rest } = props;

  return (
    <ul
      data-slot="pagination-content"
      className={getPaginationContentClassName(className)}
      {...rest}
    />
  );
}

function PaginationItem(props: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="pagination-item"
      className="flex list-none items-center"
      {...props}
    />
  );
}

function PaginationLink(props: PaginationLinkProps) {
  const { className, isActive, children, ...rest } = props;

  return (
    <Button
      type="icon"
      htmlType="button"
      size="sm"
      intent={isActive ? "secondary" : "muted"}
      className={getPaginationLinkClassName(isActive, className)}
      aria-current={isActive ? "page" : undefined}
      data-slot="pagination-link"
      data-active={isActive ? "true" : undefined}
      {...rest}
    >
      {children}
    </Button>
  );
}

function PaginationPrevious(props: PaginationPrevNextProps) {
  const { className, isActive: _isActive, ...rest } = props;

  return (
    <Button
      type="icon"
      htmlType="button"
      size="sm"
      intent="muted"
      icon={
        <IconWrapper size="xs" ariaHidden>
          <ChevronLeft />
        </IconWrapper>
      }
      className={getPaginationPrevNextClassName(className)}
      aria-label="Go to previous page"
      data-slot="pagination-previous"
      {...rest}
    />
  );
}

function PaginationNext(props: PaginationPrevNextProps) {
  const { className, isActive: _isActive, ...rest } = props;

  return (
    <Button
      type="icon"
      htmlType="button"
      size="sm"
      intent="muted"
      icon={
        <IconWrapper size="xs" ariaHidden>
          <ChevronRight />
        </IconWrapper>
      }
      className={getPaginationPrevNextClassName(className)}
      aria-label="Go to next page"
      data-slot="pagination-next"
      {...rest}
    />
  );
}

function PaginationEllipsis(props: PaginationEllipsisProps) {
  const { className, ...rest } = props;

  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={getPaginationEllipsisClassName(className)}
      {...rest}
    >
      <MoreHorizontal />
      <span className="sr-only">More pages</span>
    </span>
  );
}

/**
 * Renders numeric page buttons + ellipsis using {@link getPaginationWindow}.
 * Compose inside `PaginationContent` with `PaginationPrevious` / `PaginationNext`.
 */
function PaginationPageList({
  currentPage,
  totalPages,
  onPageChange,
  windowOptions,
}: PaginationPageListProps) {
  const pages = getPaginationWindow(currentPage, totalPages, windowOptions);

  return (
    <>
      {pages.map((page, index) =>
        page === "ellipsis" ? (
          <PaginationItem key={`ellipsis-${index}`}>
            <PaginationEllipsis />
          </PaginationItem>
        ) : (
          <PaginationItem key={page}>
            <PaginationLink
              isActive={page === currentPage}
              onClick={() =>
                onPageChange(Math.max(1, Math.min(page, totalPages)))
              }
            >
              {page}
            </PaginationLink>
          </PaginationItem>
        )
      )}
    </>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPageList,
  PaginationPrevious,
};
